/* ===========================================================================
   mojito-card-badges.js — companion to sections/mojito-card-badges.liquid

   The section prints a JSON blob; this walks the rendered page and decorates
   what matches. Three jobs:

     1. Product cards   — rule chips (NEW) and the FINAL SALE corner chip.
     2. Cart line items — a FINAL SALE chip on the cart page and drawer.
     3. Colour swatches — a NEW label inside specific swatches on the product
                          page, from that product's own metafields.

   MEASURED, NOT ASSUMED: THE CART CHIP IS NOT WHAT CALLS /cart.js
     This was pulled once on the theory that inserting a chip into a cart row
     made the drawer re-render and refetch the cart. The numbers say otherwise:
     with this code absent the draft still made 16 /cart.js requests on the cart
     page and 25 on a product page, and the PUBLISHED theme — which has none of
     this file — made 27. Counts are also identical at 8s and 12s, so they are a
     load-time burst from the cart apps, not a loop. Start with the app embeds.

     On a product page there are ZERO .cart__item elements until the drawer is
     opened, so the cart pass is a no-op there.

   WHAT IS AVOIDED, AND WHY
     Re-parenting an existing node inside .product__price. That version welded
     the saving badge into the final-sale pill and appeared to fight the theme's
     own re-render of that row. The layout problem is solved by CSS instead — a
     forced flex line break in assets/mojito-price-order.css section 6.

   THE RULE THIS FILE FOLLOWS: only ADD elements or SET attributes, and flag the
   container so a second pass is a no-op. Never delete or move theme markup.
   Note the observer watches childList only, so setting an attribute here cannot
   re-trigger it.

   OCT 2026 (SPEED AUDIT): IDLE-TIME, READ-THEN-WRITE
     The first pass was a 455 ms long task on the homepage. Two causes, both
     fixed here: (a) it ran at DOMContentLoaded, in the middle of the theme's own
     boot, so it competed with first paint and input; (b) badgeCards() read
     getComputedStyle() on a card's media box and then inserted a chip, card after
     card, which forces a fresh layout for every card (layout thrash). Now every
     pass is scheduled through requestIdleCallback (setTimeout fallback where it
     does not exist), and badgeCards() does ALL its reads for every card first,
     then ALL its writes. Badges may therefore appear a few hundred ms later on a
     busy page — they are decoration, the product image and price are not.
   =========================================================================== */
(function () {
  var holder = document.querySelector('[data-mojito-badge-rules]');
  if (!holder) return;

  var map = {}; /* handle -> { label, hideSale } */
  var finalLabel = null;
  var finalHide = false;
  var finalMap = {};
  var newColorMap = null; /* lowercased colour value -> 1 */

  try {
    var data = JSON.parse(holder.textContent) || {};

    (data.rules || []).forEach(function (rule) {
      (rule.handles || []).forEach(function (h) {
        if (!map[h]) {
          map[h] = { label: rule.label, hideSale: !!rule.hideSale };
        }
      });
    });

    var f = data.final;
    if (f && f.label && (f.handles || []).length) {
      finalLabel = f.label;
      finalHide = !!f.hideSale;
      f.handles.forEach(function (h) { finalMap[h] = 1; });
    }

    if ((data.newColors || []).length) {
      newColorMap = {};
      data.newColors.forEach(function (c) {
        newColorMap[String(c).trim().toLowerCase()] = 1;
      });
    }
  } catch (e) { return; }

  if (!Object.keys(map).length && !finalLabel && !newColorMap) return;

  function handleOf(el) {
    var a = el.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = (a.getAttribute('href') || '').match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  /* The media box is what the card chips are positioned against. The theme
     already absolutely-positions .product-badge inside it, so it is normally
     relative already — the guard is for card variants that are not. */
  function mediaHostOf(card) {
    return card.querySelector('.product__media__container')
      || card.querySelector('.product-grid-item__image')
      || card;
  }

  /* -----------------------------------------------------------------------
     1. Product cards. READ phase for every new card, then WRITE phase.
     -------------------------------------------------------------------- */
  function badgeCards() {
    var cards = document.querySelectorAll('.product-grid-item:not([data-mojito-badged])');
    if (!cards.length) return;

    var jobs = [];
    var i;
    for (i = 0; i < cards.length; i++) {
      var card = cards[i];
      var h = handleOf(card);
      var isFinal = !!(h && finalLabel && finalMap[h]);
      var rule = h ? map[h] : null;
      var job = { card: card, isFinal: isFinal, rule: rule || null, host: null, hostStatic: false, hasFinalChip: false, wrap: null };
      if (isFinal || rule) {
        job.host = mediaHostOf(card);
        job.hostStatic = getComputedStyle(job.host).position === 'static';
        job.hasFinalChip = isFinal && !!job.host.querySelector('.mojito-finalsale');
        job.wrap = rule ? card.querySelector('.product-badge') : null;
      }
      jobs.push(job);
    }

    for (i = 0; i < jobs.length; i++) {
      var jb = jobs[i];
      var c = jb.card;
      c.setAttribute('data-mojito-badged', '');
      if (!jb.host) continue;

      if (jb.hostStatic) jb.host.style.position = 'relative';

      if (jb.isFinal) {
        c.setAttribute('data-mojito-final', '');
        if (finalHide) c.setAttribute('data-mojito-nosale', '');
        if (!jb.hasFinalChip) {
          var fchip = document.createElement('div');
          fchip.className = 'mojito-finalsale';
          fchip.textContent = finalLabel;
          jb.host.appendChild(fchip);
        }
      }

      if (jb.rule) {
        if (jb.rule.hideSale) c.setAttribute('data-mojito-nosale', '');

        var wrap = jb.wrap;
        if (!wrap) {
          wrap = document.createElement('div');
          wrap.className = 'product-badge';
          jb.host.insertBefore(wrap, jb.host.firstChild);
        }

        var chip = document.createElement('div');
        chip.className = 'product-badge__item product-badge__item--mojito';
        chip.setAttribute('data-mojito-chip', '');
        chip.textContent = jb.rule.label;
        wrap.insertBefore(chip, wrap.firstChild);
      }
    }
  }

  /* -----------------------------------------------------------------------
     2. Cart line items — cart page and cart drawer.
     -------------------------------------------------------------------- */
  function badgeCartItems() {
    if (!finalLabel) return;

    var items = document.querySelectorAll('.cart__item');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.hasAttribute('data-mojito-cart-final')) continue;

      var h = handleOf(item);
      if (!h || !finalMap[h]) continue;

      var info = item.querySelector('.cart__item__info')
        || item.querySelector('.cart__item__content-inner')
        || item.querySelector('.cart__item__content');
      if (!info) continue;

      item.setAttribute('data-mojito-cart-final', '');
      if (item.querySelector('.mojito-cart-finalsale')) continue;

      var chip = document.createElement('span');
      chip.className = 'mojito-cart-finalsale';
      chip.textContent = finalLabel;
      info.appendChild(chip);
    }
  }

  /* -----------------------------------------------------------------------
     3. NEW label inside specific colour swatches, product page only.

     THE ONLY IDENTIFIER ON A SWATCH IS ITS HIDDEN RADIO'S value ATTRIBUTE.
     A swatch is <span class="radio__button"> wrapping <input class="radio__input"
     value="NATURAL"> and a <label> carrying the image. There is no colour name
     in the visible markup at all, which is why this is matched here rather than
     in CSS.

     MATCHING IS CASE- AND WHITESPACE-INSENSITIVE ON PURPOSE. The colour list is
     typed by hand into a metafield; "Natural" vs "NATURAL" failing would be a
     silent miss with no error anywhere, which is the worst way for this to
     break. What it deliberately does NOT do is fuzzy-match — a renamed colour
     option should stop matching rather than badge the wrong swatch.

     .radio__button is also the size selector's class. Size values (S, M, L)
     cannot collide with colour names in practice, and matching on the exact
     value keeps it safe even if one ever did.
     -------------------------------------------------------------------- */
  function markNewColors() {
    if (!newColorMap) return;

    var btns = document.querySelectorAll('.radio__button:not([data-mojito-newcolor])');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      var input = btn.querySelector('input.radio__input');
      if (!input) continue;

      var value = (input.getAttribute('value') || '').trim().toLowerCase();
      if (value && newColorMap[value]) {
        btn.setAttribute('data-mojito-newcolor', '');
      }
    }
  }

  /* Each job is isolated so a throw in one cannot stop the others. */
  function apply() {
    try { badgeCards(); } catch (e) {}
    try { badgeCartItems(); } catch (e) {}
    try { markNewColors(); } catch (e) {}
  }

  /* Run a pass when the main thread is idle. The timeout guarantees it still
     runs within ~1.5 s on a page that is never idle (this one, until the app
     scripts settle). setTimeout is the fallback for browsers without
     requestIdleCallback (Safari). Never requestAnimationFrame — rAF does not
     fire in a background tab and has silently killed earlier scripts here. */
  var idleQueued = false;
  function applyWhenIdle() {
    if (idleQueued) return;
    idleQueued = true;
    var run = function () { idleQueued = false; apply(); };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 50);
    }
  }

  /* THROTTLE, NOT DEBOUNCE. A trailing debounce (clearTimeout on every
     mutation) never fires on a page that mutates more often than the delay —
     animations, the hover slideshow's progress bar and app embeds all do. */
  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    setTimeout(function () {
      pending = false;
      applyWhenIdle();
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyWhenIdle);
  } else {
    applyWhenIdle();
  }

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  setTimeout(applyWhenIdle, 500);
  setTimeout(applyWhenIdle, 1500);
})();
